import { Build, Delete, Edit, ExpandLess, ExpandMore, PauseCircleOutline, TrendingUp } from '@mui/icons-material';
import {
    Box,
    Card,
    CardActionArea,
    CardActions,
    CardContent,
    Chip,
    Collapse,
    Divider,
    IconButton,
    LinearProgress,
    Tooltip,
    Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getPrinterBrandBadgeProps } from '../../helpers/printerBrand';
import {
    buildPrinterOverviewJobsUrl,
    buildPrinterOverviewMaintenancesUrl,
} from '../../helpers/printersOverview';
import { formatFilamentGrams, formatProductionDurationMinutes } from '../../helpers/jobs';
import { liveUpdateHighlightSx } from '../../helpers/liveUpdateHighlight';
import PrinterOverviewDetailsTables from './PrinterOverviewDetailsTables';
import PrinterRuntimeStatus, { idleCardSx } from './PrinterRuntimeStatus';
import PrinterSnapshotThumb from './PrinterSnapshotThumb';
import PrinterFilamentSlots from './PrinterFilamentSlots';

const linkAreaSx = {
    borderRadius: 1,
    mx: -1,
    px: 1,
    py: 0.75,
};

export default function PrinterOverviewCard({
    printer,
    period,
    highlighted = false,
    onEdit,
    onMaintenances,
    onDelete,
    onCopyUuid,
    onProductivity,
}) {
    const navigate = useNavigate();
    const [detailsOpen, setDetailsOpen] = useState(false);
    const brandBadge = getPrinterBrandBadgeProps(printer.brand);
    const successRate = Number(printer.success_rate) || 0;
    const printerId = printer.printer_uuid || printer.id;
    const jobsUrl = buildPrinterOverviewJobsUrl(printerId, period);
    const maintenancesUrl = buildPrinterOverviewMaintenancesUrl(printerId);

    return (
        <Card
            variant="outlined"
            sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                ...idleCardSx(printer.is_printing, printer.is_idle, printer.productivity_restriction),
                ...liveUpdateHighlightSx(highlighted),
            }}
        >
            <PrinterSnapshotThumb
                printer={printer}
                alt={`Câmera de ${printer.name || 'impressora'}`}
            />
            <CardContent sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, mb: 1.5 }}>
                    <Box sx={{ minWidth: 0 }}>
                        {brandBadge ? (
                            <Chip label={printer.name} size="small" {...brandBadge} />
                        ) : (
                            <Typography variant="subtitle1" fontWeight={700} noWrap>
                                {printer.name}
                            </Typography>
                        )}
                    </Box>
                    <PrinterRuntimeStatus
                        isPrinting={printer.is_printing}
                        isIdle={printer.is_idle}
                        idleLabel={printer.idle_label}
                        lastActivityAt={printer.last_activity_at}
                        restriction={printer.productivity_restriction}
                    />
                </Box>

                <CardActionArea
                    onClick={() => navigate(jobsUrl)}
                    sx={linkAreaSx}
                    aria-label={`Ver impressões de ${printer.name}`}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                        <TrendingUp fontSize="small" color="primary" />
                        <Typography variant="body2" color="text.secondary">
                            Taxa de sucesso
                        </Typography>
                    </Box>
                    <Typography variant="h5" fontWeight={800} sx={{ mb: 0.5 }}>
                        {successRate.toFixed(1).replace('.', ',')}%
                        <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                            ({printer.total_jobs} impressões)
                        </Typography>
                    </Typography>
                    <LinearProgress
                        variant="determinate"
                        value={Math.min(100, successRate)}
                        sx={{ height: 6, borderRadius: 1 }}
                    />
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, lineHeight: 1.55 }}>
                        Tempo estimado:{' '}
                        <strong>{formatProductionDurationMinutes(printer.total_duration_minutes)}</strong>
                        {' · '}
                        Filamento: <strong>{formatFilamentGrams(printer.total_filament_grams)}</strong>
                    </Typography>
                </CardActionArea>

                <Box sx={{ mt: 1.5 }}>
                    <PrinterFilamentSlots
                        printerUuid={printerId}
                        printerName={printer.name}
                        filaments={printer.filaments}
                        restriction={printer.productivity_restriction}
                    />
                </Box>

                <Box sx={{ mt: 1.25 }}>
                    <Chip
                        size="small"
                        variant="outlined"
                        icon={detailsOpen ? <ExpandLess /> : <ExpandMore />}
                        label={detailsOpen ? 'Ocultar detalhes' : 'Mais detalhes'}
                        onClick={() => setDetailsOpen((prev) => !prev)}
                    />
                </Box>

                <Collapse in={detailsOpen}>
                    <Box sx={{ mt: 1.25, pt: 1.25, borderTop: 1, borderColor: 'divider' }}>
                        <PrinterOverviewDetailsTables
                            printer={printer}
                            onCopyUuid={onCopyUuid}
                            onOpenMaintenances={() => navigate(maintenancesUrl)}
                        />
                    </Box>
                </Collapse>
            </CardContent>

            <Divider />
            <CardActions sx={{ justifyContent: 'flex-end', px: 1 }}>
                <Tooltip title="Editar">
                    <IconButton color="primary" onClick={() => onEdit?.(printerId)} aria-label="editar">
                        <Edit />
                    </IconButton>
                </Tooltip>
                <Tooltip title={printer.productivity_restriction ? 'Restrição de produção' : 'Marcar como não produtiva'}>
                    <IconButton
                        color={printer.productivity_restriction ? 'warning' : 'primary'}
                        onClick={() => onProductivity?.(printer)}
                        aria-label="restrição de produção"
                    >
                        <PauseCircleOutline />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Manutenções">
                    <IconButton color="primary" onClick={() => onMaintenances?.(printerId)} aria-label="manutenções">
                        <Build />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Remover">
                    <IconButton color="error" onClick={() => onDelete?.(printerId)} aria-label="remover">
                        <Delete />
                    </IconButton>
                </Tooltip>
            </CardActions>
        </Card>
    );
}
